import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';
import LoadingScreen from '../components/LoadingScreen';
import { FiHeart, FiShare2, FiCopy, FiAlertCircle } from 'react-icons/fi';

/* ─── Brand tokens ─── */
const CORAL   = '#E3425C';
const CHARCOAL = '#1C1C1E';
const GOLD    = '#C9A84C';
const WARM_WHITE = '#FAFAF9';

/* ─── SVG Decoration Components ─── */
const FlowerSVG = ({ size = 40, color = '#E91E63', opacity = 0.3, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" style={{ opacity, ...style }}>
    <circle cx="20" cy="14" r="6" fill={color} />
    <circle cx="14" cy="20" r="6" fill={color} />
    <circle cx="26" cy="20" r="6" fill={color} />
    <circle cx="17" cy="26" r="6" fill={color} />
    <circle cx="23" cy="26" r="6" fill={color} />
    <circle cx="20" cy="20" r="4" fill="#FFD700" />
  </svg>
);

const RoseSVG = ({ size = 50, color = '#E91E63', opacity = 0.25, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 50 50" style={{ opacity, ...style }}>
    <circle cx="25" cy="25" r="8" fill={color} opacity="0.6" />
    <circle cx="25" cy="25" r="5" fill={color} opacity="0.8" />
    <circle cx="25" cy="25" r="2.5" fill={color} />
    <ellipse cx="25" cy="15" rx="5" ry="8" fill={color} opacity="0.4" transform="rotate(0, 25, 25)" />
    <ellipse cx="35" cy="25" rx="5" ry="8" fill={color} opacity="0.4" transform="rotate(90, 35, 25)" />
    <ellipse cx="25" cy="35" rx="5" ry="8" fill={color} opacity="0.4" transform="rotate(0, 25, 35)" />
    <ellipse cx="15" cy="25" rx="5" ry="8" fill={color} opacity="0.4" transform="rotate(90, 15, 25)" />
    <ellipse cx="18" cy="18" rx="4" ry="7" fill={color} opacity="0.3" transform="rotate(-45, 18, 18)" />
    <ellipse cx="32" cy="18" rx="4" ry="7" fill={color} opacity="0.3" transform="rotate(45, 32, 18)" />
    <ellipse cx="32" cy="32" rx="4" ry="7" fill={color} opacity="0.3" transform="rotate(-45, 32, 32)" />
    <ellipse cx="18" cy="32" rx="4" ry="7" fill={color} opacity="0.3" transform="rotate(45, 18, 32)" />
  </svg>
);

const LeafSVG = ({ size = 30, color = '#5C7A5A', opacity = 0.4, rotation = 0, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 30 30" style={{ opacity, transform: `rotate(${rotation}deg)`, ...style }}>
    <path d="M15 2 C8 8, 4 16, 8 24 C10 22, 12 18, 15 15 C18 18, 20 22, 22 24 C26 16, 22 8, 15 2Z" fill={color} />
    <line x1="15" y1="5" x2="15" y2="22" stroke={color} strokeWidth="0.8" opacity="0.6" />
  </svg>
);

const DaisySVG = ({ size = 35, color = '#FFFFFF', centerColor = '#FFD700', opacity = 0.3, style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 35 35" style={{ opacity, ...style }}>
    {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
      <ellipse key={i} cx="17.5" cy="8" rx="3.5" ry="7" fill={color}
        transform={`rotate(${angle}, 17.5, 17.5)`} />
    ))}
    <circle cx="17.5" cy="17.5" r="4" fill={centerColor} />
  </svg>
);

const VineSVG = ({ width = 20, height = 200, color = '#5C7A5A', opacity = 0.2, style = {} }) => (
  <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ opacity, ...style }}>
    <path d={`M10 0 C10 20, 5 40, 10 60 C15 80, 5 100, 10 120 C15 140, 5 160, 10 180 C12 190, 10 200, 10 200`}
      stroke={color} fill="none" strokeWidth="1.5" />
    {[30, 70, 110, 150].map((y, i) => (
      <g key={i}>
        <path d={`M10 ${y} C${i % 2 === 0 ? '0' : '20'} ${y - 5}, ${i % 2 === 0 ? '-2' : '22'} ${y - 12}, ${i % 2 === 0 ? '3' : '17'} ${y - 15}`}
          stroke={color} fill="none" strokeWidth="1" />
        <ellipse cx={i % 2 === 0 ? 2 : 18} cy={y - 14} rx="4" ry="6" fill={color} opacity="0.5"
          transform={`rotate(${i % 2 === 0 ? -30 : 30}, ${i % 2 === 0 ? 2 : 18}, ${y - 14})`} />
      </g>
    ))}
  </svg>
);

const FloralBorderSVG = ({ width = 400, height = 20, color = '#E91E63', opacity = 0.15, style = {} }) => (
  <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ opacity, ...style }}>
    {Array.from({ length: 20 }).map((_, i) => (
      <g key={i}>
        <circle cx={i * 20 + 10} cy="10" r="3" fill={color} opacity="0.5" />
        <circle cx={i * 20 + 5} cy="7" r="2" fill={color} opacity="0.3" />
        <circle cx={i * 20 + 15} cy="7" r="2" fill={color} opacity="0.3" />
        <circle cx={i * 20 + 5} cy="13" r="2" fill={color} opacity="0.3" />
        <circle cx={i * 20 + 15} cy="13" r="2" fill={color} opacity="0.3" />
      </g>
    ))}
  </svg>
);

/* ─── Card style themes (10 total) ─── */
const CARD_THEMES = {
  elegant: {
    pageBg: CHARCOAL,
    cardBg: 'linear-gradient(145deg, #1C1C1E 0%, #2A2A2C 50%, #1C1C1E 100%)',
    titleColor: GOLD,
    textColor: '#E8E3DA',
    accentColor: GOLD,
    borderColor: GOLD,
    signatureColor: GOLD,
    subtitleColor: '#A9A49B',
    overlayGradient: 'linear-gradient(to bottom, rgba(28,28,30,0.2) 0%, rgba(28,28,30,0.85) 100%)',
    cardShadow: `0 0 60px rgba(201,168,76,0.15), 0 20px 60px rgba(0,0,0,0.5)`,
    borderStyle: `1px solid ${GOLD}`,
    fontHeading: "'DM Serif Display', serif",
  },
  floral: {
    pageBg: '#FDF2F4',
    cardBg: 'linear-gradient(145deg, #FFF7F8 0%, #FDEEF2 50%, #FCE4EC 100%)',
    titleColor: '#C2185B',
    textColor: '#5D4037',
    accentColor: '#E91E63',
    borderColor: '#F8BBD0',
    signatureColor: '#AD1457',
    subtitleColor: '#8D6E63',
    overlayGradient: 'linear-gradient(to bottom, rgba(253,242,244,0.1) 0%, rgba(252,228,236,0.9) 100%)',
    cardShadow: '0 8px 40px rgba(194,24,91,0.1), 0 2px 12px rgba(0,0,0,0.05)',
    borderStyle: '2px solid #F8BBD0',
    fontHeading: "'DM Serif Display', serif",
  },
  golden: {
    pageBg: '#2C1810',
    cardBg: `linear-gradient(145deg, ${GOLD} 0%, #D4AF37 30%, #E8C547 50%, #D4AF37 70%, ${GOLD} 100%)`,
    titleColor: '#FFFFFF',
    textColor: '#FFFFFF',
    accentColor: '#FFFFFF',
    borderColor: 'rgba(255,255,255,0.3)',
    signatureColor: '#FFFFFF',
    subtitleColor: 'rgba(255,255,255,0.8)',
    overlayGradient: 'linear-gradient(to bottom, rgba(201,168,76,0.1) 0%, rgba(201,168,76,0.92) 100%)',
    cardShadow: `0 0 80px rgba(201,168,76,0.4), 0 20px 60px rgba(0,0,0,0.3)`,
    borderStyle: '2px solid rgba(255,255,255,0.3)',
    fontHeading: "'DM Serif Display', serif",
  },
  minimal: {
    pageBg: WARM_WHITE,
    cardBg: '#FFFFFF',
    titleColor: CHARCOAL,
    textColor: '#444444',
    accentColor: CORAL,
    borderColor: '#E0E0E0',
    signatureColor: CHARCOAL,
    subtitleColor: '#888888',
    overlayGradient: 'linear-gradient(to bottom, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.92) 100%)',
    cardShadow: '0 1px 3px rgba(0,0,0,0.06), 0 12px 40px rgba(0,0,0,0.06)',
    borderStyle: '1px solid #E0E0E0',
    fontHeading: "'DM Serif Display', serif",
  },
  festive: {
    pageBg: '#FFF8F0',
    cardBg: 'linear-gradient(145deg, #FFFCF7 0%, #FFF5EB 50%, #FFF0E0 100%)',
    titleColor: CORAL,
    textColor: CHARCOAL,
    accentColor: GOLD,
    borderColor: CORAL,
    signatureColor: CORAL,
    subtitleColor: '#666666',
    overlayGradient: 'linear-gradient(to bottom, rgba(255,248,240,0.1) 0%, rgba(255,240,224,0.92) 100%)',
    cardShadow: `0 8px 40px rgba(227,66,92,0.12), 0 4px 16px rgba(201,168,76,0.1)`,
    borderStyle: `2px solid ${CORAL}`,
    fontHeading: "'DM Serif Display', serif",
  },
  botanical: {
    pageBg: '#E8F0E8',
    cardBg: 'linear-gradient(145deg, #FAFDF8 0%, #F0F7F0 50%, #E8F2E6 100%)',
    titleColor: '#3D5A3D',
    textColor: '#4A5D4A',
    accentColor: '#5C7A5A',
    borderColor: '#A8C5A0',
    signatureColor: '#3D5A3D',
    subtitleColor: '#6B8A68',
    overlayGradient: 'linear-gradient(to bottom, rgba(240,247,240,0.1) 0%, rgba(232,242,230,0.92) 100%)',
    cardShadow: '0 8px 40px rgba(92,122,90,0.12), 0 2px 12px rgba(0,0,0,0.04)',
    borderStyle: '2px solid #A8C5A0',
    fontHeading: "'DM Serif Display', serif",
  },
  roseGold: {
    pageBg: '#2A1F22',
    cardBg: 'linear-gradient(145deg, #B76E79 0%, #D4939C 30%, #E8B4B8 50%, #D4939C 70%, #B76E79 100%)',
    titleColor: '#FFFFFF',
    textColor: '#FFFFFF',
    accentColor: '#FFE4E8',
    borderColor: 'rgba(255,255,255,0.25)',
    signatureColor: '#FFFFFF',
    subtitleColor: 'rgba(255,255,255,0.8)',
    overlayGradient: 'linear-gradient(to bottom, rgba(183,110,121,0.1) 0%, rgba(183,110,121,0.9) 100%)',
    cardShadow: '0 0 60px rgba(183,110,121,0.3), 0 20px 60px rgba(0,0,0,0.3)',
    borderStyle: '2px solid rgba(255,255,255,0.25)',
    fontHeading: "'DM Serif Display', serif",
  },
  african: {
    pageBg: '#3A2010',
    cardBg: 'linear-gradient(145deg, #4A2C1A 0%, #5C3820 50%, #4A2C1A 100%)',
    titleColor: '#CC6633',
    textColor: '#E8D5C4',
    accentColor: '#CC6633',
    borderColor: '#CC6633',
    signatureColor: '#D4A574',
    subtitleColor: '#B89A7E',
    overlayGradient: 'linear-gradient(to bottom, rgba(74,44,26,0.2) 0%, rgba(74,44,26,0.9) 100%)',
    cardShadow: '0 0 60px rgba(204,102,51,0.15), 0 20px 60px rgba(0,0,0,0.4)',
    borderStyle: '3px solid #CC6633',
    fontHeading: "'DM Serif Display', serif",
  },
  midnight: {
    pageBg: '#080E18',
    cardBg: 'linear-gradient(145deg, #0D1B2A 0%, #152238 30%, #1A0A2E 70%, #0D1B2A 100%)',
    titleColor: '#C0C0FF',
    textColor: '#B8BED0',
    accentColor: '#C0C0FF',
    borderColor: 'rgba(192,192,255,0.2)',
    signatureColor: '#C0C0FF',
    subtitleColor: 'rgba(192,192,255,0.6)',
    overlayGradient: 'linear-gradient(to bottom, rgba(13,27,42,0.2) 0%, rgba(26,10,46,0.9) 100%)',
    cardShadow: '0 0 60px rgba(192,192,255,0.1), 0 20px 60px rgba(0,0,0,0.5)',
    borderStyle: '1px solid rgba(192,192,255,0.2)',
    fontHeading: "'DM Serif Display', serif",
  },
  garden: {
    pageBg: '#E4EFE4',
    cardBg: 'linear-gradient(145deg, #F8FDF8 0%, #F0F7F0 50%, #E8F5E6 100%)',
    titleColor: '#3A6B3A',
    textColor: '#4A5E4A',
    accentColor: '#6BAF6B',
    borderColor: '#B8D8B0',
    signatureColor: '#3A6B3A',
    subtitleColor: '#7A9A78',
    overlayGradient: 'linear-gradient(to bottom, rgba(240,247,240,0.1) 0%, rgba(232,245,230,0.92) 100%)',
    cardShadow: '0 8px 40px rgba(58,107,58,0.1), 0 2px 12px rgba(0,0,0,0.04)',
    borderStyle: '2px solid #B8D8B0',
    fontHeading: "'DM Serif Display', serif",
  },
};

/* ─── Dark-background themes for header/footer text ─── */
const DARK_BG_THEMES = ['elegant', 'golden', 'roseGold', 'african', 'midnight'];

/* ─── Keyframe animations (injected once) ─── */
const STYLE_ID = 'thank-you-card-styles';

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes tyCardFadeIn {
      0% { opacity: 0; transform: scale(0.92) translateY(30px); }
      100% { opacity: 1; transform: scale(1) translateY(0); }
    }
    @keyframes tyFloat {
      0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0; }
      10% { opacity: 1; }
      90% { opacity: 1; }
      100% { transform: translateY(-100vh) rotate(360deg); opacity: 0; }
    }
    @keyframes tyPulse {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 1; }
    }
    @keyframes tyShimmer {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
    @keyframes tySparkle {
      0%, 100% { transform: scale(0) rotate(0deg); opacity: 0; }
      50% { transform: scale(1) rotate(180deg); opacity: 1; }
    }
    @keyframes tyConfettiDrift {
      0% { transform: translateY(0) rotate(0deg); opacity: 0; }
      5% { opacity: 1; }
      100% { transform: translateY(-110vh) rotate(720deg); opacity: 0; }
    }
    @keyframes tyGlow {
      0%, 100% { box-shadow: 0 0 20px rgba(201,168,76,0.2); }
      50% { box-shadow: 0 0 40px rgba(201,168,76,0.4), 0 0 80px rgba(201,168,76,0.15); }
    }
    @keyframes tyFloralCorner {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.05); }
    }
    @keyframes tyHeartBeat {
      0%, 100% { transform: scale(1); }
      15% { transform: scale(1.15); }
      30% { transform: scale(1); }
      45% { transform: scale(1.1); }
    }
    @keyframes tyTwinkle {
      0%, 100% { opacity: 0.2; transform: scale(0.8); }
      50% { opacity: 1; transform: scale(1.2); }
    }
    @keyframes tyShimmerStripe {
      0% { transform: translateX(-100%) rotate(-45deg); }
      100% { transform: translateX(200%) rotate(-45deg); }
    }
    @keyframes tyLeafSway {
      0%, 100% { transform: rotate(-5deg); }
      50% { transform: rotate(5deg); }
    }
    @keyframes tyStarFloat {
      0%, 100% { transform: translateY(0) scale(1); opacity: 0; }
      10% { opacity: 0.8; }
      50% { transform: translateY(-40vh) scale(1.2); opacity: 1; }
      90% { opacity: 0.8; }
      100% { transform: translateY(-100vh) scale(0.6); opacity: 0; }
    }
    .ty-page * { box-sizing: border-box; }
  `;
  document.head.appendChild(style);
}

/* ─── Floating particles component ─── */
function FloatingParticles({ cardStyle }) {
  const particles = [];
  const count = 14;

  for (let i = 0; i < count; i++) {
    const left = Math.random() * 100;
    const delay = Math.random() * 8;
    const duration = 6 + Math.random() * 8;
    const size = 4 + Math.random() * 8;

    if (cardStyle === 'festive') {
      const colors = [CORAL, GOLD, '#2BBAA0', '#8B5CF6', '#F59E0B'];
      const color = colors[i % colors.length];
      const width = 4 + Math.random() * 6;
      const height = 6 + Math.random() * 10;
      particles.push(
        <div key={i} style={{
          position: 'absolute', bottom: '-20px',
          left: `${left}%`,
          width: `${width}px`, height: `${height}px`,
          background: color, borderRadius: '1px',
          animation: `tyConfettiDrift ${duration}s ${delay}s ease-in infinite`,
          opacity: 0, pointerEvents: 'none',
        }} />
      );
    } else if (cardStyle === 'golden' || cardStyle === 'roseGold') {
      particles.push(
        <div key={i} style={{
          position: 'absolute',
          left: `${left}%`, top: `${Math.random() * 100}%`,
          width: `${size}px`, height: `${size}px`,
          background: cardStyle === 'roseGold'
            ? 'radial-gradient(circle, rgba(255,228,232,0.9), transparent 70%)'
            : 'radial-gradient(circle, rgba(255,255,255,0.9), transparent 70%)',
          borderRadius: '50%',
          animation: `tySparkle ${3 + Math.random() * 4}s ${delay}s ease-in-out infinite`,
          opacity: 0, pointerEvents: 'none',
        }} />
      );
    } else if (cardStyle === 'botanical') {
      particles.push(
        <div key={i} style={{
          position: 'absolute', bottom: '-20px',
          left: `${left}%`,
          fontSize: `${size + 6}px`,
          color: '#5C7A5A',
          animation: `tyFloat ${duration}s ${delay}s ease-in infinite`,
          opacity: 0, pointerEvents: 'none',
        }}>
          {'\uD83C\uDF43'}
        </div>
      );
    } else if (cardStyle === 'african') {
      particles.push(
        <div key={i} style={{
          position: 'absolute', bottom: '-20px',
          left: `${left}%`,
          fontSize: `${size + 2}px`,
          color: i % 2 === 0 ? '#CC6633' : '#D4A574',
          animation: `tyFloat ${duration}s ${delay}s ease-in infinite`,
          opacity: 0, pointerEvents: 'none',
        }}>
          {'\u25C6'}
        </div>
      );
    } else if (cardStyle === 'midnight') {
      particles.push(
        <div key={i} style={{
          position: 'absolute',
          left: `${left}%`, top: `${Math.random() * 100}%`,
          fontSize: `${size + 2}px`,
          color: i % 3 === 0 ? '#C0C0FF' : i % 3 === 1 ? '#FFD700' : '#FFFFFF',
          animation: `tyStarFloat ${duration + 4}s ${delay}s ease-in-out infinite`,
          opacity: 0, pointerEvents: 'none',
        }}>
          {i % 2 === 0 ? '\u2726' : '\u2B51'}
        </div>
      );
    } else if (cardStyle === 'garden') {
      particles.push(
        <div key={i} style={{
          position: 'absolute', bottom: '-20px',
          left: `${left}%`,
          fontSize: `${size + 4}px`,
          color: i % 3 === 0 ? '#6BAF6B' : i % 3 === 1 ? '#E91E63' : '#FFD700',
          animation: `tyFloat ${duration}s ${delay}s ease-in infinite`,
          opacity: 0, pointerEvents: 'none',
        }}>
          {'\u273F'}
        </div>
      );
    } else {
      const isHeart = cardStyle === 'floral' || cardStyle === 'elegant';
      particles.push(
        <div key={i} style={{
          position: 'absolute', bottom: '-20px',
          left: `${left}%`,
          fontSize: `${size + 4}px`,
          color: cardStyle === 'elegant' ? GOLD :
                 cardStyle === 'floral' ? '#E91E63' :
                 cardStyle === 'minimal' ? CORAL : GOLD,
          animation: `tyFloat ${duration}s ${delay}s ease-in infinite`,
          opacity: 0, pointerEvents: 'none',
        }}>
          {isHeart ? '\u2665' : '\u2726'}
        </div>
      );
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0,
      overflow: 'hidden', pointerEvents: 'none', zIndex: 1,
    }}>
      {particles}
    </div>
  );
}

/* ─── Decorative overlays per theme ─── */
function CardDecorations({ cardStyle, theme }) {
  if (cardStyle === 'elegant') {
    return (
      <>
        {/* Top filigree border */}
        <div style={{
          position: 'absolute', top: 0, left: '5%', right: '5%', height: '3px',
          background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
          animation: 'tyPulse 3s ease-in-out infinite',
        }} />
        {/* Bottom filigree border */}
        <div style={{
          position: 'absolute', bottom: 0, left: '5%', right: '5%', height: '3px',
          background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
          animation: 'tyPulse 3s ease-in-out 1.5s infinite',
        }} />
        {/* Vertical side lines - more visible */}
        <div style={{
          position: 'absolute', top: '8%', left: 0, bottom: '8%', width: '2px',
          background: `linear-gradient(180deg, transparent, rgba(201,168,76,0.5), transparent)`,
        }} />
        <div style={{
          position: 'absolute', top: '8%', right: 0, bottom: '8%', width: '2px',
          background: `linear-gradient(180deg, transparent, rgba(201,168,76,0.5), transparent)`,
        }} />
        {/* Corner bracket frames - larger and more visible */}
        {['topLeft', 'topRight', 'bottomLeft', 'bottomRight'].map(corner => {
          const isTop = corner.includes('top');
          const isLeft = corner.includes('Left');
          return (
            <div key={corner} style={{
              position: 'absolute',
              [isTop ? 'top' : 'bottom']: '12px',
              [isLeft ? 'left' : 'right']: '12px',
              width: '40px', height: '40px',
              borderTop: isTop ? `2px solid ${GOLD}` : 'none',
              borderBottom: !isTop ? `2px solid ${GOLD}` : 'none',
              borderLeft: isLeft ? `2px solid ${GOLD}` : 'none',
              borderRight: !isLeft ? `2px solid ${GOLD}` : 'none',
              opacity: 0.8,
            }} />
          );
        })}
        {/* Gold floral scrollwork - top-left corner */}
        <div style={{ position: 'absolute', top: '4px', left: '4px', pointerEvents: 'none', opacity: 0.6 }}>
          <svg width="70" height="70" viewBox="0 0 70 70">
            <path d="M5 5 C5 5, 15 5, 20 10 C25 15, 22 25, 15 25 C10 25, 8 20, 10 17 C12 14, 16 15, 16 18" stroke={GOLD} fill="none" strokeWidth="1.5" />
            <path d="M5 5 C5 5, 5 15, 10 20 C15 25, 25 22, 25 15 C25 10, 20 8, 17 10 C14 12, 15 16, 18 16" stroke={GOLD} fill="none" strokeWidth="1.5" />
            <circle cx="16" cy="18" r="2" fill={GOLD} opacity="0.5" />
            <path d="M20 10 C28 8, 35 12, 38 20" stroke={GOLD} fill="none" strokeWidth="1" opacity="0.4" />
            <path d="M10 20 C8 28, 12 35, 20 38" stroke={GOLD} fill="none" strokeWidth="1" opacity="0.4" />
            {/* Small leaf curls */}
            <path d="M30 15 C34 10, 40 8, 42 12 C38 14, 34 14, 30 15Z" fill={GOLD} opacity="0.3" />
            <path d="M15 30 C10 34, 8 40, 12 42 C14 38, 14 34, 15 30Z" fill={GOLD} opacity="0.3" />
          </svg>
        </div>
        {/* Gold floral scrollwork - top-right corner */}
        <div style={{ position: 'absolute', top: '4px', right: '4px', pointerEvents: 'none', opacity: 0.6, transform: 'scaleX(-1)' }}>
          <svg width="70" height="70" viewBox="0 0 70 70">
            <path d="M5 5 C5 5, 15 5, 20 10 C25 15, 22 25, 15 25 C10 25, 8 20, 10 17 C12 14, 16 15, 16 18" stroke={GOLD} fill="none" strokeWidth="1.5" />
            <path d="M5 5 C5 5, 5 15, 10 20 C15 25, 25 22, 25 15 C25 10, 20 8, 17 10 C14 12, 15 16, 18 16" stroke={GOLD} fill="none" strokeWidth="1.5" />
            <circle cx="16" cy="18" r="2" fill={GOLD} opacity="0.5" />
            <path d="M20 10 C28 8, 35 12, 38 20" stroke={GOLD} fill="none" strokeWidth="1" opacity="0.4" />
            <path d="M10 20 C8 28, 12 35, 20 38" stroke={GOLD} fill="none" strokeWidth="1" opacity="0.4" />
            <path d="M30 15 C34 10, 40 8, 42 12 C38 14, 34 14, 30 15Z" fill={GOLD} opacity="0.3" />
            <path d="M15 30 C10 34, 8 40, 12 42 C14 38, 14 34, 15 30Z" fill={GOLD} opacity="0.3" />
          </svg>
        </div>
        {/* Gold floral scrollwork - bottom-left corner */}
        <div style={{ position: 'absolute', bottom: '4px', left: '4px', pointerEvents: 'none', opacity: 0.6, transform: 'scaleY(-1)' }}>
          <svg width="70" height="70" viewBox="0 0 70 70">
            <path d="M5 5 C5 5, 15 5, 20 10 C25 15, 22 25, 15 25 C10 25, 8 20, 10 17 C12 14, 16 15, 16 18" stroke={GOLD} fill="none" strokeWidth="1.5" />
            <path d="M5 5 C5 5, 5 15, 10 20 C15 25, 25 22, 25 15 C25 10, 20 8, 17 10 C14 12, 15 16, 18 16" stroke={GOLD} fill="none" strokeWidth="1.5" />
            <circle cx="16" cy="18" r="2" fill={GOLD} opacity="0.5" />
            <path d="M20 10 C28 8, 35 12, 38 20" stroke={GOLD} fill="none" strokeWidth="1" opacity="0.4" />
            <path d="M10 20 C8 28, 12 35, 20 38" stroke={GOLD} fill="none" strokeWidth="1" opacity="0.4" />
            <path d="M30 15 C34 10, 40 8, 42 12 C38 14, 34 14, 30 15Z" fill={GOLD} opacity="0.3" />
            <path d="M15 30 C10 34, 8 40, 12 42 C14 38, 14 34, 15 30Z" fill={GOLD} opacity="0.3" />
          </svg>
        </div>
        {/* Gold floral scrollwork - bottom-right corner */}
        <div style={{ position: 'absolute', bottom: '4px', right: '4px', pointerEvents: 'none', opacity: 0.6, transform: 'scale(-1, -1)' }}>
          <svg width="70" height="70" viewBox="0 0 70 70">
            <path d="M5 5 C5 5, 15 5, 20 10 C25 15, 22 25, 15 25 C10 25, 8 20, 10 17 C12 14, 16 15, 16 18" stroke={GOLD} fill="none" strokeWidth="1.5" />
            <path d="M5 5 C5 5, 5 15, 10 20 C15 25, 25 22, 25 15 C25 10, 20 8, 17 10 C14 12, 15 16, 18 16" stroke={GOLD} fill="none" strokeWidth="1.5" />
            <circle cx="16" cy="18" r="2" fill={GOLD} opacity="0.5" />
            <path d="M20 10 C28 8, 35 12, 38 20" stroke={GOLD} fill="none" strokeWidth="1" opacity="0.4" />
            <path d="M10 20 C8 28, 12 35, 20 38" stroke={GOLD} fill="none" strokeWidth="1" opacity="0.4" />
            <path d="M30 15 C34 10, 40 8, 42 12 C38 14, 34 14, 30 15Z" fill={GOLD} opacity="0.3" />
            <path d="M15 30 C10 34, 8 40, 12 42 C14 38, 14 34, 15 30Z" fill={GOLD} opacity="0.3" />
          </svg>
        </div>
        {/* Gold crown/diamond motif at top center */}
        <div style={{ position: 'absolute', top: '6px', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none', opacity: 0.6 }}>
          <svg width="60" height="30" viewBox="0 0 60 30">
            {/* Diamond */}
            <polygon points="30,2 38,15 30,28 22,15" fill="none" stroke={GOLD} strokeWidth="1.5" />
            <polygon points="30,6 35,15 30,24 25,15" fill={GOLD} opacity="0.25" />
            {/* Side flourishes */}
            <path d="M22 15 C16 12, 10 14, 5 12" stroke={GOLD} fill="none" strokeWidth="1" />
            <path d="M38 15 C44 12, 50 14, 55 12" stroke={GOLD} fill="none" strokeWidth="1" />
            {/* Tiny dots at ends */}
            <circle cx="5" cy="12" r="1.5" fill={GOLD} opacity="0.5" />
            <circle cx="55" cy="12" r="1.5" fill={GOLD} opacity="0.5" />
          </svg>
        </div>
        {/* Decorative filigree along top edge */}
        <div style={{ position: 'absolute', top: '3px', left: '20%', right: '20%', pointerEvents: 'none', opacity: 0.35 }}>
          <svg width="100%" height="8" viewBox="0 0 200 8" preserveAspectRatio="none">
            {Array.from({ length: 20 }).map((_, i) => (
              <path key={`ft${i}`} d={`M${i * 10} 4 Q${i * 10 + 5} 0, ${i * 10 + 10} 4`} stroke={GOLD} fill="none" strokeWidth="0.8" />
            ))}
          </svg>
        </div>
        {/* Decorative filigree along bottom edge */}
        <div style={{ position: 'absolute', bottom: '3px', left: '20%', right: '20%', pointerEvents: 'none', opacity: 0.35 }}>
          <svg width="100%" height="8" viewBox="0 0 200 8" preserveAspectRatio="none">
            {Array.from({ length: 20 }).map((_, i) => (
              <path key={`fb${i}`} d={`M${i * 10} 4 Q${i * 10 + 5} 8, ${i * 10 + 10} 4`} stroke={GOLD} fill="none" strokeWidth="0.8" />
            ))}
          </svg>
        </div>
      </>
    );
  }

  if (cardStyle === 'floral') {
    return (
      <>
        {/* Bottom-right floral bouquet */}
        <div style={{ position: 'absolute', bottom: '0', right: '0', pointerEvents: 'none', opacity: 0.85 }}>
          <svg width="140" height="150" viewBox="0 0 140 150">
            {/* Large rose */}
            <circle cx="90" cy="110" r="22" fill="#F48FB1" opacity="0.5" />
            <circle cx="90" cy="110" r="15" fill="#E91E63" opacity="0.45" />
            <circle cx="90" cy="110" r="9" fill="#C2185B" opacity="0.4" />
            <circle cx="90" cy="110" r="4" fill="#AD1457" opacity="0.5" />
            {/* Medium peony */}
            {[0, 72, 144, 216, 288].map((angle, i) => (
              <ellipse key={`p${i}`} cx={50 + Math.cos(angle * Math.PI / 180) * 14} cy={125 + Math.sin(angle * Math.PI / 180) * 14}
                rx="9" ry="14" fill="#F8BBD0" opacity="0.45"
                transform={`rotate(${angle}, ${50 + Math.cos(angle * Math.PI / 180) * 14}, ${125 + Math.sin(angle * Math.PI / 180) * 14})`} />
            ))}
            <circle cx="50" cy="125" r="7" fill="#E91E63" opacity="0.4" />
            {/* Small bud top */}
            <circle cx="110" cy="80" r="8" fill="#F48FB1" opacity="0.4" />
            <circle cx="110" cy="80" r="4" fill="#E91E63" opacity="0.35" />
            {/* Leaves */}
            <path d="M120 95 C130 80, 138 65, 135 72 C130 78, 125 88, 120 95Z" fill="#4CAF50" opacity="0.45" />
            <path d="M60 105 C45 90, 30 80, 25 88 C35 92, 48 98, 60 105Z" fill="#66BB6A" opacity="0.4" />
            <path d="M30 130 C18 120, 8 108, 5 115 C12 120, 22 126, 30 130Z" fill="#4CAF50" opacity="0.35" />
            {/* Baby's breath */}
            {[[25, 110], [35, 100], [70, 95], [105, 90], [120, 105], [75, 140], [110, 135], [40, 140]].map(([x, y], i) => (
              <circle key={`b${i}`} cx={x} cy={y} r="2.5" fill="white" opacity="0.5" />
            ))}
          </svg>
        </div>
        {/* Bottom-left smaller floral accent */}
        <div style={{ position: 'absolute', bottom: '0', left: '0', pointerEvents: 'none', opacity: 0.7 }}>
          <svg width="90" height="100" viewBox="0 0 90 100">
            {/* Small rose */}
            <circle cx="30" cy="75" r="14" fill="#F48FB1" opacity="0.5" />
            <circle cx="30" cy="75" r="8" fill="#E91E63" opacity="0.4" />
            {/* Daisy */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
              <ellipse key={i} cx="60" cy="90" rx="4" ry="9" fill="white" opacity="0.5"
                transform={`rotate(${angle}, 60, 90)`} />
            ))}
            <circle cx="60" cy="90" r="5" fill="#FFD700" opacity="0.55" />
            {/* Leaf */}
            <path d="M10 65 C0 50, 2 35, 12 30 C12 40, 10 52, 10 65Z" fill="#66BB6A" opacity="0.45" />
            <path d="M75 75 C85 65, 88 52, 82 50 C80 58, 78 68, 75 75Z" fill="#4CAF50" opacity="0.4" />
          </svg>
        </div>
        {/* Top corners — small flower accents */}
        <div style={{ position: 'absolute', top: '4px', left: '4px', pointerEvents: 'none' }}>
          <RoseSVG size={50} color="#E91E63" opacity={0.4} />
        </div>
        <div style={{ position: 'absolute', top: '4px', right: '4px', pointerEvents: 'none' }}>
          <FlowerSVG size={40} color="#F48FB1" opacity={0.45} />
        </div>
        {/* Scattered small daisies along sides */}
        <div style={{ position: 'absolute', top: '35%', left: '3px', pointerEvents: 'none' }}>
          <DaisySVG size={24} color="white" centerColor="#FFD700" opacity={0.45} />
        </div>
        <div style={{ position: 'absolute', top: '55%', right: '3px', pointerEvents: 'none' }}>
          <DaisySVG size={20} color="white" centerColor="#FFD700" opacity={0.4} />
        </div>
        {/* Floral borders */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, pointerEvents: 'none' }}>
          <FloralBorderSVG color="#E91E63" opacity={0.2} />
        </div>
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, pointerEvents: 'none' }}>
          <FloralBorderSVG color="#E91E63" opacity={0.2} />
        </div>
      </>
    );
  }

  if (cardStyle === 'golden') {
    return (
      <>
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          width: '120%', height: '120%',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 60%)',
          animation: 'tyGlow 4s ease-in-out infinite',
          pointerEvents: 'none', zIndex: -1,
        }} />
        {/* Double-line borders */}
        <div style={{
          position: 'absolute', top: '12px', left: '12px', right: '12px', bottom: '12px',
          border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', top: '16px', left: '16px', right: '16px',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
        }} />
        <div style={{
          position: 'absolute', bottom: '16px', left: '16px', right: '16px',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
        }} />
      </>
    );
  }

  if (cardStyle === 'festive') {
    const confetti = [];
    const colors = [CORAL, GOLD, '#2BBAA0', '#8B5CF6', '#F59E0B', '#FF6B9D', '#00BCD4', '#FF9800'];
    for (let i = 0; i < 30; i++) {
      const size = 5 + Math.random() * 8;
      const isRect = i % 3 === 0;
      const rotation = Math.random() * 360;
      confetti.push(
        <div key={i} style={{
          position: 'absolute',
          top: `${Math.random() * 100}%`, left: `${Math.random() * 100}%`,
          width: `${isRect ? size * 0.5 : size}px`, height: `${size}px`,
          background: colors[i % colors.length],
          borderRadius: isRect ? '2px' : '50%',
          opacity: 0.25 + Math.random() * 0.2,
          transform: `rotate(${rotation}deg)`,
          pointerEvents: 'none',
        }} />
      );
    }
    // Streamer ribbons
    const streamers = [];
    const streamerColors = ['#FF6B9D', '#FFD700', '#2BBAA0', '#8B5CF6'];
    for (let i = 0; i < 6; i++) {
      const startX = 10 + Math.random() * 80;
      streamers.push(
        <div key={`str${i}`} style={{ position: 'absolute', top: 0, left: `${startX}%`, pointerEvents: 'none', opacity: 0.3 }}>
          <svg width="30" height="80" viewBox="0 0 30 80">
            <path d={`M15 0 Q5 20, 20 35 Q8 50, 18 65 Q10 75, 15 80`}
              stroke={streamerColors[i % streamerColors.length]} fill="none" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
      );
    }
    return (
      <>
        {confetti}
        {streamers}
        {/* Balloon - top-left */}
        <div style={{ position: 'absolute', top: '5px', left: '8px', pointerEvents: 'none', opacity: 0.5 }}>
          <svg width="35" height="55" viewBox="0 0 35 55">
            <ellipse cx="17" cy="18" rx="14" ry="17" fill="#FF6B9D" />
            <ellipse cx="17" cy="18" rx="14" ry="17" fill="none" stroke="#FF4081" strokeWidth="0.5" />
            <ellipse cx="12" cy="12" rx="4" ry="6" fill="rgba(255,255,255,0.25)" transform="rotate(-20, 12, 12)" />
            <polygon points="14,34 17,38 20,34" fill="#FF6B9D" />
            <path d="M17 38 Q15 45, 18 50 Q16 53, 17 55" stroke="#FF6B9D" fill="none" strokeWidth="0.8" />
          </svg>
        </div>
        {/* Balloon - top-right */}
        <div style={{ position: 'absolute', top: '10px', right: '10px', pointerEvents: 'none', opacity: 0.5 }}>
          <svg width="30" height="50" viewBox="0 0 30 50">
            <ellipse cx="15" cy="16" rx="12" ry="15" fill="#8B5CF6" />
            <ellipse cx="15" cy="16" rx="12" ry="15" fill="none" stroke="#7C3AED" strokeWidth="0.5" />
            <ellipse cx="11" cy="11" rx="3" ry="5" fill="rgba(255,255,255,0.25)" transform="rotate(-20, 11, 11)" />
            <polygon points="12,30 15,34 18,30" fill="#8B5CF6" />
            <path d="M15 34 Q13 40, 16 45 Q14 48, 15 50" stroke="#8B5CF6" fill="none" strokeWidth="0.8" />
          </svg>
        </div>
        {/* Balloon - bottom-left */}
        <div style={{ position: 'absolute', bottom: '15px', left: '5px', pointerEvents: 'none', opacity: 0.45 }}>
          <svg width="28" height="45" viewBox="0 0 28 45">
            <ellipse cx="14" cy="14" rx="11" ry="13" fill="#2BBAA0" />
            <ellipse cx="10" cy="10" rx="3" ry="4" fill="rgba(255,255,255,0.2)" transform="rotate(-20, 10, 10)" />
            <polygon points="11,26 14,30 17,26" fill="#2BBAA0" />
            <path d="M14 30 Q12 36, 15 40 Q13 43, 14 45" stroke="#2BBAA0" fill="none" strokeWidth="0.8" />
          </svg>
        </div>
        {/* Balloon - bottom-right */}
        <div style={{ position: 'absolute', bottom: '10px', right: '8px', pointerEvents: 'none', opacity: 0.45 }}>
          <svg width="32" height="48" viewBox="0 0 32 48">
            <ellipse cx="16" cy="15" rx="13" ry="14" fill="#F59E0B" />
            <ellipse cx="12" cy="10" rx="3" ry="5" fill="rgba(255,255,255,0.25)" transform="rotate(-20, 12, 10)" />
            <polygon points="13,28 16,32 19,28" fill="#F59E0B" />
            <path d="M16 32 Q14 38, 17 42 Q15 45, 16 48" stroke="#F59E0B" fill="none" strokeWidth="0.8" />
          </svg>
        </div>
        {/* Star burst accents */}
        {[[50, 8], [85, 40], [15, 60]].map(([l, t], i) => (
          <div key={`star${i}`} style={{ position: 'absolute', top: `${t}%`, left: `${l}%`, pointerEvents: 'none', opacity: 0.4 }}>
            <svg width="16" height="16" viewBox="0 0 16 16">
              <polygon points="8,0 10,6 16,6 11,10 13,16 8,12 3,16 5,10 0,6 6,6"
                fill={colors[(i + 2) % colors.length]} />
            </svg>
          </div>
        ))}
      </>
    );
  }

  if (cardStyle === 'botanical') {
    return (
      <>
        {/* Bottom-right botanical cluster */}
        <div style={{ position: 'absolute', bottom: '0', right: '0', pointerEvents: 'none', opacity: 0.8 }}>
          <svg width="130" height="140" viewBox="0 0 130 140">
            {/* Large eucalyptus branch */}
            <path d="M100 10 C95 30, 85 50, 75 70 C70 80, 60 95, 55 110 C52 120, 50 130, 48 140" stroke="#5C7A5A" fill="none" strokeWidth="2" opacity="0.5" />
            {/* Eucalyptus leaves along branch */}
            <ellipse cx="105" cy="18" rx="8" ry="14" fill="#7BA67B" opacity="0.45" transform="rotate(-20, 105, 18)" />
            <ellipse cx="95" cy="35" rx="7" ry="12" fill="#5C7A5A" opacity="0.5" transform="rotate(15, 95, 35)" />
            <ellipse cx="87" cy="52" rx="9" ry="13" fill="#6B8A68" opacity="0.45" transform="rotate(-10, 87, 52)" />
            <ellipse cx="78" cy="72" rx="8" ry="14" fill="#5C7A5A" opacity="0.5" transform="rotate(20, 78, 72)" />
            <ellipse cx="65" cy="90" rx="10" ry="15" fill="#7BA67B" opacity="0.4" transform="rotate(-15, 65, 90)" />
            <ellipse cx="55" cy="112" rx="8" ry="12" fill="#5C7A5A" opacity="0.45" transform="rotate(10, 55, 112)" />
            {/* Second smaller branch */}
            <path d="M120 50 C110 65, 95 80, 80 95" stroke="#6B8A68" fill="none" strokeWidth="1.5" opacity="0.4" />
            <ellipse cx="118" cy="55" rx="6" ry="10" fill="#5C7A5A" opacity="0.4" transform="rotate(-30, 118, 55)" />
            <ellipse cx="108" cy="68" rx="7" ry="11" fill="#7BA67B" opacity="0.35" transform="rotate(25, 108, 68)" />
            <ellipse cx="95" cy="82" rx="6" ry="10" fill="#6B8A68" opacity="0.4" transform="rotate(-20, 95, 82)" />
            {/* Small berry accents */}
            {[[112, 42], [92, 45], [82, 62], [72, 82], [58, 100], [110, 60]].map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r="2.5" fill="#8D6E63" opacity="0.45" />
            ))}
          </svg>
        </div>
        {/* Bottom-left botanical accent */}
        <div style={{ position: 'absolute', bottom: '0', left: '0', pointerEvents: 'none', opacity: 0.7, transform: 'scaleX(-1)' }}>
          <svg width="80" height="100" viewBox="0 0 80 100">
            <path d="M60 5 C55 25, 45 45, 40 65 C38 75, 35 85, 33 100" stroke="#5C7A5A" fill="none" strokeWidth="1.5" opacity="0.5" />
            <ellipse cx="62" cy="12" rx="7" ry="12" fill="#7BA67B" opacity="0.45" transform="rotate(-15, 62, 12)" />
            <ellipse cx="55" cy="30" rx="8" ry="13" fill="#5C7A5A" opacity="0.5" transform="rotate(20, 55, 30)" />
            <ellipse cx="47" cy="50" rx="7" ry="11" fill="#6B8A68" opacity="0.4" transform="rotate(-10, 47, 50)" />
            <ellipse cx="40" cy="70" rx="8" ry="12" fill="#5C7A5A" opacity="0.45" transform="rotate(15, 40, 70)" />
          </svg>
        </div>
        {/* Top corners — small leaves */}
        <div style={{ position: 'absolute', top: '5px', left: '5px', pointerEvents: 'none' }}>
          <LeafSVG size={38} color="#5C7A5A" opacity={0.5} rotation={-25} />
        </div>
        <div style={{ position: 'absolute', top: '5px', right: '5px', pointerEvents: 'none' }}>
          <LeafSVG size={35} color="#6B8A68" opacity={0.45} rotation={25} />
        </div>
        <div style={{ position: 'absolute', top: '50px', left: '3px', pointerEvents: 'none' }}>
          <LeafSVG size={25} color="#7BA67B" opacity={0.35} rotation={-40} />
        </div>
        {/* Vine trailing down right side */}
        <div style={{ position: 'absolute', top: '8px', right: '1px', pointerEvents: 'none' }}>
          <VineSVG width={20} height={200} color="#5C7A5A" opacity={0.3} />
        </div>
        {/* Top/bottom green accents */}
        <div style={{
          position: 'absolute', top: 0, left: '12%', right: '12%', height: '2px',
          background: 'linear-gradient(90deg, transparent, #5C7A5A, transparent)',
          opacity: 0.4,
        }} />
        <div style={{
          position: 'absolute', bottom: 0, left: '12%', right: '12%', height: '2px',
          background: 'linear-gradient(90deg, transparent, #5C7A5A, transparent)',
          opacity: 0.4,
        }} />
      </>
    );
  }

  if (cardStyle === 'roseGold') {
    // Scattered pearl dots
    const pearls = [];
    for (let i = 0; i < 15; i++) {
      pearls.push(
        <div key={`pearl${i}`} style={{
          position: 'absolute',
          top: `${10 + Math.random() * 80}%`, left: `${10 + Math.random() * 80}%`,
          width: `${3 + Math.random() * 3}px`, height: `${3 + Math.random() * 3}px`,
          background: 'radial-gradient(circle, rgba(255,255,255,0.7), rgba(255,220,220,0.3))',
          borderRadius: '50%',
          opacity: 0.35 + Math.random() * 0.2,
          pointerEvents: 'none',
        }} />
      );
    }
    return (
      <>
        {/* Shimmer stripe animation */}
        <div style={{
          position: 'absolute', inset: 0, overflow: 'hidden',
          borderRadius: '20px', pointerEvents: 'none',
        }}>
          <div style={{
            position: 'absolute', top: '-50%', left: '-50%',
            width: '40px', height: '200%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)',
            animation: 'tyShimmerStripe 4s ease-in-out infinite',
            pointerEvents: 'none',
          }} />
        </div>
        {/* Double border inset */}
        <div style={{
          position: 'absolute', top: '10px', left: '10px', right: '10px', bottom: '10px',
          border: '1px solid rgba(255,255,255,0.2)', borderRadius: '14px',
          pointerEvents: 'none',
        }} />
        {/* Corner diamonds - more visible */}
        {[
          { top: '6px', left: '6px' },
          { top: '6px', right: '6px' },
          { bottom: '6px', left: '6px' },
          { bottom: '6px', right: '6px' },
        ].map((pos, i) => (
          <div key={i} style={{
            position: 'absolute', ...pos,
            width: '10px', height: '10px',
            background: 'rgba(255,200,200,0.45)',
            transform: 'rotate(45deg)',
            pointerEvents: 'none',
          }} />
        ))}
        {/* Rose flower - bottom-right corner */}
        <div style={{ position: 'absolute', bottom: '0', right: '0', pointerEvents: 'none', opacity: 0.55 }}>
          <svg width="100" height="110" viewBox="0 0 100 110">
            {/* Large rose bloom */}
            <circle cx="60" cy="75" r="22" fill="#E8A0B4" opacity="0.4" />
            <circle cx="60" cy="75" r="16" fill="#D4839A" opacity="0.45" />
            <circle cx="60" cy="75" r="10" fill="#C77D90" opacity="0.4" />
            <circle cx="60" cy="75" r="5" fill="#B8687E" opacity="0.5" />
            {/* Rose petals layered */}
            {[0, 72, 144, 216, 288].map((angle, i) => (
              <ellipse key={`rp${i}`}
                cx={60 + Math.cos(angle * Math.PI / 180) * 18}
                cy={75 + Math.sin(angle * Math.PI / 180) * 18}
                rx="8" ry="12" fill="#E8A0B4" opacity="0.3"
                transform={`rotate(${angle}, ${60 + Math.cos(angle * Math.PI / 180) * 18}, ${75 + Math.sin(angle * Math.PI / 180) * 18})`} />
            ))}
            {/* Small bud */}
            <circle cx="30" cy="95" r="10" fill="#D4839A" opacity="0.35" />
            <circle cx="30" cy="95" r="6" fill="#C77D90" opacity="0.3" />
            {/* Leaves */}
            <path d="M80 60 C92 48, 98 38, 95 45 C90 50, 85 55, 80 60Z" fill="#7BA67B" opacity="0.35" />
            <path d="M15 85 C5 78, 0 65, 5 70 C10 75, 12 80, 15 85Z" fill="#6B8A68" opacity="0.3" />
            <path d="M45 58 C40 45, 42 35, 48 38 C48 44, 46 50, 45 58Z" fill="#7BA67B" opacity="0.3" />
            {/* Stem */}
            <path d="M60 55 C58 45, 55 35, 52 25" stroke="#7BA67B" fill="none" strokeWidth="1.2" opacity="0.3" />
          </svg>
        </div>
        {/* Rose flower - top-left corner */}
        <div style={{ position: 'absolute', top: '0', left: '0', pointerEvents: 'none', opacity: 0.45 }}>
          <svg width="80" height="85" viewBox="0 0 80 85">
            {/* Medium rose */}
            <circle cx="30" cy="35" r="16" fill="#E8A0B4" opacity="0.4" />
            <circle cx="30" cy="35" r="10" fill="#D4839A" opacity="0.45" />
            <circle cx="30" cy="35" r="5" fill="#C77D90" opacity="0.4" />
            {/* Petals */}
            {[0, 90, 180, 270].map((angle, i) => (
              <ellipse key={`tp${i}`}
                cx={30 + Math.cos(angle * Math.PI / 180) * 14}
                cy={35 + Math.sin(angle * Math.PI / 180) * 14}
                rx="6" ry="10" fill="#E8A0B4" opacity="0.25"
                transform={`rotate(${angle}, ${30 + Math.cos(angle * Math.PI / 180) * 14}, ${35 + Math.sin(angle * Math.PI / 180) * 14})`} />
            ))}
            {/* Small bud */}
            <circle cx="60" cy="20" r="7" fill="#D4839A" opacity="0.3" />
            <circle cx="60" cy="20" r="4" fill="#C77D90" opacity="0.25" />
            {/* Leaf */}
            <path d="M50 50 C58 55, 68 52, 72 48 C65 50, 58 50, 50 50Z" fill="#7BA67B" opacity="0.3" />
            <path d="M15 55 C8 62, 5 72, 8 68 C12 63, 14 58, 15 55Z" fill="#6B8A68" opacity="0.25" />
          </svg>
        </div>
        {/* Pearl dots scattered */}
        {pearls}
        {/* Subtle rose-gold glow bottom-left */}
        <div style={{
          position: 'absolute', bottom: '-10px', left: '-10px',
          width: '80px', height: '80px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(228,160,180,0.15), transparent 70%)',
          pointerEvents: 'none',
        }} />
      </>
    );
  }

  if (cardStyle === 'african') {
    return (
      <>
        {/* Geometric border pattern - top (taller, more colorful) */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '22px',
          overflow: 'hidden', pointerEvents: 'none',
        }}>
          <svg width="100%" height="22" viewBox="0 0 400 22" preserveAspectRatio="none" style={{ display: 'block' }}>
            {/* Bottom row of triangles */}
            {Array.from({ length: 25 }).map((_, i) => (
              <polygon key={`t${i}`} points={`${i * 16},22 ${i * 16 + 8},6 ${i * 16 + 16},22`}
                fill={i % 3 === 0 ? '#CC6633' : i % 3 === 1 ? '#D4A574' : '#E8B84B'} opacity="0.65" />
            ))}
            {/* Top accent line */}
            <rect x="0" y="0" width="400" height="3" fill="#CC6633" opacity="0.5" />
            <rect x="0" y="3" width="400" height="2" fill="#E8B84B" opacity="0.4" />
          </svg>
        </div>
        {/* Geometric border pattern - bottom (taller, more colorful) */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: '22px',
          overflow: 'hidden', pointerEvents: 'none',
        }}>
          <svg width="100%" height="22" viewBox="0 0 400 22" preserveAspectRatio="none" style={{ display: 'block' }}>
            {Array.from({ length: 25 }).map((_, i) => (
              <polygon key={`b${i}`} points={`${i * 16},0 ${i * 16 + 8},16 ${i * 16 + 16},0`}
                fill={i % 3 === 0 ? '#CC6633' : i % 3 === 1 ? '#D4A574' : '#E8B84B'} opacity="0.65" />
            ))}
            <rect x="0" y="19" width="400" height="3" fill="#CC6633" opacity="0.5" />
            <rect x="0" y="17" width="400" height="2" fill="#E8B84B" opacity="0.4" />
          </svg>
        </div>
        {/* Side diamond strips - larger and more colorful */}
        {['left', 'right'].map(side => (
          <div key={side} style={{
            position: 'absolute', top: '26px', [side]: '4px', bottom: '26px',
            width: '14px', pointerEvents: 'none',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'space-evenly',
          }}>
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} style={{
                width: '9px', height: '9px',
                background: i % 3 === 0 ? '#CC6633' : i % 3 === 1 ? GOLD : '#D4A574',
                transform: 'rotate(45deg)',
                opacity: 0.55,
              }} />
            ))}
          </div>
        ))}
        {/* Ndebele-inspired corner pattern - bottom-left */}
        <div style={{ position: 'absolute', bottom: '24px', left: '18px', pointerEvents: 'none', opacity: 0.6 }}>
          <svg width="65" height="65" viewBox="0 0 65 65">
            {/* Nested rectangles - Ndebele style */}
            <rect x="2" y="2" width="60" height="60" fill="none" stroke="#CC6633" strokeWidth="2" />
            <rect x="8" y="8" width="48" height="48" fill="none" stroke="#E8B84B" strokeWidth="2" />
            <rect x="14" y="14" width="36" height="36" fill="none" stroke="#D4A574" strokeWidth="2" />
            <rect x="20" y="20" width="24" height="24" fill="none" stroke="#CC6633" strokeWidth="1.5" />
            {/* Center diamond */}
            <polygon points="32,22 42,32 32,42 22,32" fill="#E8B84B" opacity="0.4" />
            <polygon points="32,22 42,32 32,42 22,32" fill="none" stroke="#CC6633" strokeWidth="1" />
            {/* Corner accents */}
            <rect x="2" y="2" width="10" height="10" fill="#CC6633" opacity="0.35" />
            <rect x="52" y="2" width="10" height="10" fill="#E8B84B" opacity="0.35" />
            <rect x="2" y="52" width="10" height="10" fill="#E8B84B" opacity="0.35" />
            <rect x="52" y="52" width="10" height="10" fill="#CC6633" opacity="0.35" />
          </svg>
        </div>
        {/* Ndebele-inspired corner pattern - bottom-right */}
        <div style={{ position: 'absolute', bottom: '24px', right: '18px', pointerEvents: 'none', opacity: 0.6 }}>
          <svg width="65" height="65" viewBox="0 0 65 65">
            <rect x="2" y="2" width="60" height="60" fill="none" stroke="#D4A574" strokeWidth="2" />
            <rect x="8" y="8" width="48" height="48" fill="none" stroke="#CC6633" strokeWidth="2" />
            <rect x="14" y="14" width="36" height="36" fill="none" stroke="#E8B84B" strokeWidth="2" />
            <rect x="20" y="20" width="24" height="24" fill="none" stroke="#D4A574" strokeWidth="1.5" />
            {/* Center X cross */}
            <line x1="20" y1="20" x2="44" y2="44" stroke="#CC6633" strokeWidth="1.5" opacity="0.5" />
            <line x1="44" y1="20" x2="20" y2="44" stroke="#CC6633" strokeWidth="1.5" opacity="0.5" />
            <circle cx="32" cy="32" r="4" fill="#E8B84B" opacity="0.45" />
            {/* Corner accents */}
            <rect x="2" y="2" width="10" height="10" fill="#E8B84B" opacity="0.35" />
            <rect x="52" y="2" width="10" height="10" fill="#CC6633" opacity="0.35" />
            <rect x="2" y="52" width="10" height="10" fill="#CC6633" opacity="0.35" />
            <rect x="52" y="52" width="10" height="10" fill="#E8B84B" opacity="0.35" />
          </svg>
        </div>
        {/* Ndebele-inspired pattern - top-left */}
        <div style={{ position: 'absolute', top: '24px', left: '18px', pointerEvents: 'none', opacity: 0.5 }}>
          <svg width="45" height="45" viewBox="0 0 45 45">
            <rect x="2" y="2" width="40" height="40" fill="none" stroke="#CC6633" strokeWidth="1.5" />
            <rect x="8" y="8" width="28" height="28" fill="none" stroke="#E8B84B" strokeWidth="1.5" />
            <polygon points="22,10 34,22 22,34 10,22" fill="none" stroke="#D4A574" strokeWidth="1.5" />
            <circle cx="22" cy="22" r="3" fill="#CC6633" opacity="0.45" />
          </svg>
        </div>
        {/* Ndebele-inspired pattern - top-right */}
        <div style={{ position: 'absolute', top: '24px', right: '18px', pointerEvents: 'none', opacity: 0.5 }}>
          <svg width="45" height="45" viewBox="0 0 45 45">
            <rect x="2" y="2" width="40" height="40" fill="none" stroke="#D4A574" strokeWidth="1.5" />
            <rect x="8" y="8" width="28" height="28" fill="none" stroke="#CC6633" strokeWidth="1.5" />
            <polygon points="22,10 34,22 22,34 10,22" fill="none" stroke="#E8B84B" strokeWidth="1.5" />
            <circle cx="22" cy="22" r="3" fill="#E8B84B" opacity="0.45" />
          </svg>
        </div>
        {/* Inner decorative frame - more visible */}
        <div style={{
          position: 'absolute', top: '24px', left: '20px', right: '20px', bottom: '24px',
          border: '1.5px solid rgba(204,102,51,0.3)',
          pointerEvents: 'none',
        }} />
        {/* Second inner frame for double-line effect */}
        <div style={{
          position: 'absolute', top: '28px', left: '24px', right: '24px', bottom: '28px',
          border: '1px solid rgba(232,184,75,0.2)',
          pointerEvents: 'none',
        }} />
      </>
    );
  }

  if (cardStyle === 'midnight') {
    const stars = [];
    const starPositions = [];
    for (let i = 0; i < 35; i++) {
      const size = 2 + Math.random() * 4;
      const top = Math.random() * 100;
      const left = Math.random() * 100;
      starPositions.push({ top, left, size });
      const colors = ['#FFD700', '#FFA500', '#C0C0FF', '#E0E0FF', '#FFFFFF', '#87CEEB', '#DDA0DD'];
      stars.push(
        <div key={i} style={{
          position: 'absolute',
          top: `${top}%`, left: `${left}%`,
          width: `${size}px`, height: `${size}px`,
          background: colors[i % colors.length],
          borderRadius: '50%',
          boxShadow: size > 3.5 ? `0 0 ${size * 2}px ${colors[i % colors.length]}` : 'none',
          opacity: 0.5 + Math.random() * 0.4,
          animation: `tyTwinkle ${2 + Math.random() * 4}s ${Math.random() * 3}s ease-in-out infinite`,
          pointerEvents: 'none',
        }} />
      );
    }
    // Build constellation lines connecting nearby stars
    const constellationLines = [];
    for (let i = 0; i < starPositions.length - 1; i++) {
      const a = starPositions[i];
      const b = starPositions[i + 1];
      const dist = Math.sqrt(Math.pow(a.top - b.top, 2) + Math.pow(a.left - b.left, 2));
      if (dist < 25 && constellationLines.length < 8) {
        constellationLines.push({ x1: a.left, y1: a.top, x2: b.left, y2: b.top, key: `cl${i}` });
      }
    }
    return (
      <>
        {stars}
        {/* Constellation lines */}
        <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
          {constellationLines.map(l => (
            <line key={l.key} x1={`${l.x1}%`} y1={`${l.y1}%`} x2={`${l.x2}%`} y2={`${l.y2}%`}
              stroke="rgba(192,192,255,0.25)" strokeWidth="0.8" strokeDasharray="3,4" />
          ))}
        </svg>
        {/* Crescent moon top-right */}
        <div style={{ position: 'absolute', top: '8px', right: '12px', pointerEvents: 'none', opacity: 0.7 }}>
          <svg width="50" height="50" viewBox="0 0 50 50">
            <defs>
              <radialGradient id="moonGlow" cx="50%" cy="50%" r="70%">
                <stop offset="0%" stopColor="#FFFACD" stopOpacity="0.3" />
                <stop offset="100%" stopColor="transparent" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="25" cy="25" r="24" fill="url(#moonGlow)" />
            <path d="M30 5 A20 20 0 1 0 30 45 A14 14 0 1 1 30 5Z" fill="#FFFACD" opacity="0.6" />
            <path d="M30 5 A20 20 0 1 0 30 45 A14 14 0 1 1 30 5Z" fill="none" stroke="#FFE082" strokeWidth="0.5" opacity="0.4" />
          </svg>
        </div>
        {/* Galaxy swirl bottom-left */}
        <div style={{ position: 'absolute', bottom: '0', left: '0', pointerEvents: 'none', opacity: 0.55 }}>
          <svg width="120" height="120" viewBox="0 0 120 120">
            <defs>
              <radialGradient id="galaxyGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#7B68EE" stopOpacity="0.4" />
                <stop offset="40%" stopColor="#4B0082" stopOpacity="0.2" />
                <stop offset="100%" stopColor="transparent" stopOpacity="0" />
              </radialGradient>
            </defs>
            <circle cx="60" cy="60" r="55" fill="url(#galaxyGrad)" />
            {/* Spiral arms */}
            <path d="M60 60 Q70 40, 90 35 Q80 50, 85 65 Q75 55, 60 60Z" fill="#9370DB" opacity="0.25" />
            <path d="M60 60 Q50 80, 30 85 Q40 70, 35 55 Q45 65, 60 60Z" fill="#7B68EE" opacity="0.2" />
            <path d="M60 60 Q40 45, 35 25 Q48 38, 55 30 Q52 45, 60 60Z" fill="#6A5ACD" opacity="0.15" />
            {/* Bright center */}
            <circle cx="60" cy="60" r="4" fill="#E0E0FF" opacity="0.5" />
            <circle cx="60" cy="60" r="2" fill="#FFFFFF" opacity="0.6" />
            {/* Tiny galaxy stars */}
            {[[45,35], [80,45], [35,75], [75,80], [50,50], [70,55], [40,60], [85,55], [55,85], [30,45]].map(([x,y], i) => (
              <circle key={`gs${i}`} cx={x} cy={y} r={1 + Math.random()} fill={i % 2 === 0 ? '#DDA0DD' : '#E0E0FF'} opacity={0.4 + Math.random() * 0.3} />
            ))}
          </svg>
        </div>
        {/* Nebula glow bottom-right */}
        <div style={{ position: 'absolute', bottom: '-15px', right: '-10px', pointerEvents: 'none', opacity: 0.4 }}>
          <svg width="100" height="100" viewBox="0 0 100 100">
            <defs>
              <radialGradient id="nebulaGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#4169E1" stopOpacity="0.35" />
                <stop offset="50%" stopColor="#191970" stopOpacity="0.15" />
                <stop offset="100%" stopColor="transparent" stopOpacity="0" />
              </radialGradient>
            </defs>
            <ellipse cx="50" cy="50" rx="48" ry="40" fill="url(#nebulaGrad)" />
            {/* Tiny stars in nebula */}
            {[[30,35], [60,25], [45,55], [70,50], [55,70], [25,60]].map(([x,y], i) => (
              <circle key={`ns${i}`} cx={x} cy={y} r="1.5" fill="#87CEEB" opacity="0.5" />
            ))}
          </svg>
        </div>
        {/* Enhanced moon glow */}
        <div style={{
          position: 'absolute', top: '-10px', right: '-10px',
          width: '100px', height: '100px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,250,205,0.12), rgba(192,192,255,0.06), transparent 70%)',
          pointerEvents: 'none',
        }} />
      </>
    );
  }

  if (cardStyle === 'garden') {
    return (
      <>
        {/* Watercolor wash background blobs */}
        <div style={{
          position: 'absolute', top: '-30px', left: '-30px',
          width: '140px', height: '140px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(107,175,107,0.12), transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', top: '25%', right: '-40px',
          width: '150px', height: '150px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(184,216,176,0.1), transparent 70%)',
          pointerEvents: 'none',
        }} />
        {/* Bottom-left floral cluster */}
        <div style={{ position: 'absolute', bottom: '0', left: '0', pointerEvents: 'none', opacity: 0.85 }}>
          <svg width="120" height="130" viewBox="0 0 120 130">
            {/* Large rose */}
            <circle cx="35" cy="100" r="18" fill="#E8A0B4" opacity="0.5" />
            <circle cx="35" cy="100" r="12" fill="#D4839A" opacity="0.6" />
            <circle cx="35" cy="100" r="7" fill="#C06880" opacity="0.5" />
            {/* Medium flower */}
            {[0, 60, 120, 180, 240, 300].map((angle, i) => (
              <ellipse key={i} cx={65 + Math.cos(angle * Math.PI / 180) * 10} cy={115 + Math.sin(angle * Math.PI / 180) * 10}
                rx="6" ry="10" fill="#B8D8B0" opacity="0.45"
                transform={`rotate(${angle}, ${65 + Math.cos(angle * Math.PI / 180) * 10}, ${115 + Math.sin(angle * Math.PI / 180) * 10})`} />
            ))}
            <circle cx="65" cy="115" r="5" fill="#FFD700" opacity="0.5" />
            {/* Leaves */}
            <path d="M15 85 C5 75, 0 60, 10 50 C12 55, 14 65, 15 85Z" fill="#5C7A5A" opacity="0.45" />
            <path d="M55 90 C65 75, 75 65, 85 70 C78 75, 65 82, 55 90Z" fill="#6BAF6B" opacity="0.4" />
            <path d="M85 105 C95 95, 110 90, 115 95 C108 98, 95 100, 85 105Z" fill="#5C7A5A" opacity="0.35" />
            {/* Small buds */}
            <circle cx="15" cy="115" r="5" fill="#D4839A" opacity="0.4" />
            <circle cx="90" cy="125" r="4" fill="#E8A0B4" opacity="0.35" />
            {/* Stem lines */}
            <path d="M35 82 C30 60, 20 45, 15 30" stroke="#5C7A5A" fill="none" strokeWidth="1.2" opacity="0.3" />
            <path d="M65 100 C60 85, 55 70, 50 55" stroke="#6BAF6B" fill="none" strokeWidth="1" opacity="0.25" />
          </svg>
        </div>
        {/* Bottom-right floral accent */}
        <div style={{ position: 'absolute', bottom: '0', right: '0', pointerEvents: 'none', opacity: 0.75 }}>
          <svg width="100" height="110" viewBox="0 0 100 110">
            {/* Daisy flower */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
              <ellipse key={i} cx="65" cy="75" rx="5" ry="12" fill="white" opacity="0.5"
                transform={`rotate(${angle}, 65, 75)`} />
            ))}
            <circle cx="65" cy="75" r="6" fill="#FFD700" opacity="0.55" />
            {/* Leaves curling */}
            <path d="M80 60 C90 50, 98 42, 100 48 C95 52, 88 56, 80 60Z" fill="#5C7A5A" opacity="0.45" />
            <path d="M50 65 C40 55, 35 42, 42 38 C44 45, 48 55, 50 65Z" fill="#6BAF6B" opacity="0.4" />
            {/* Small pink buds */}
            <circle cx="40" cy="95" r="6" fill="#E8A0B4" opacity="0.4" />
            <circle cx="85" cy="100" r="4" fill="#D4839A" opacity="0.35" />
            {/* Baby's breath dots */}
            {[[30, 85], [45, 105], [75, 105], [90, 90], [55, 60], [80, 50]].map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r="2" fill="white" opacity="0.4" />
            ))}
          </svg>
        </div>
        {/* Top-left small leaf accent */}
        <div style={{ position: 'absolute', top: '5px', left: '5px', pointerEvents: 'none', opacity: 0.6 }}>
          <svg width="60" height="60" viewBox="0 0 60 60">
            <path d="M5 55 C5 35, 15 15, 35 5 C30 15, 20 30, 5 55Z" fill="#6BAF6B" opacity="0.5" />
            <path d="M10 50 C15 35, 25 20, 40 12" stroke="#5C7A5A" fill="none" strokeWidth="0.8" opacity="0.4" />
            <circle cx="48" cy="8" r="3" fill="#E8A0B4" opacity="0.5" />
          </svg>
        </div>
        {/* Top-right leaf accent */}
        <div style={{ position: 'absolute', top: '5px', right: '5px', pointerEvents: 'none', opacity: 0.55, transform: 'scaleX(-1)' }}>
          <svg width="50" height="50" viewBox="0 0 50 50">
            <path d="M5 45 C5 28, 12 12, 28 5 C25 15, 18 28, 5 45Z" fill="#5C7A5A" opacity="0.5" />
            <circle cx="35" cy="8" r="3" fill="#FFD700" opacity="0.5" />
          </svg>
        </div>
        {/* Subtle green top & bottom line */}
        <div style={{
          position: 'absolute', top: 0, left: '20%', right: '20%', height: '2px',
          background: 'linear-gradient(90deg, transparent, #6BAF6B, transparent)',
          opacity: 0.35,
        }} />
        <div style={{
          position: 'absolute', bottom: 0, left: '20%', right: '20%', height: '2px',
          background: 'linear-gradient(90deg, transparent, #6BAF6B, transparent)',
          opacity: 0.35,
        }} />
      </>
    );
  }

  // minimal -- no decorations
  return null;
}

/* ─── Separator ─── */
function ThemeDivider({ theme, cardStyle }) {
  if (cardStyle === 'elegant') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        margin: '24px 0', justifyContent: 'center',
      }}>
        <div style={{ flex: '1', maxWidth: '60px', height: '1px', background: `linear-gradient(to right, transparent, ${GOLD})` }} />
        <span style={{ color: GOLD, fontSize: '12px', letterSpacing: '4px' }}>{'\u2726'}</span>
        <div style={{ flex: '1', maxWidth: '60px', height: '1px', background: `linear-gradient(to left, transparent, ${GOLD})` }} />
      </div>
    );
  }
  if (cardStyle === 'floral') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        margin: '24px 0', justifyContent: 'center',
      }}>
        <div style={{ flex: '1', maxWidth: '50px', height: '1px', background: 'linear-gradient(to right, transparent, #F8BBD0)' }} />
        <FlowerSVG size={22} color="#E91E63" opacity={0.6} />
        <div style={{ flex: '1', maxWidth: '50px', height: '1px', background: 'linear-gradient(to left, transparent, #F8BBD0)' }} />
      </div>
    );
  }
  if (cardStyle === 'golden') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        margin: '24px 0', justifyContent: 'center',
      }}>
        <div style={{ flex: '1', maxWidth: '60px', height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.5))' }} />
        <span style={{ color: '#FFFFFF', fontSize: '10px', letterSpacing: '6px' }}>{'\u25C6 \u25C6 \u25C6'}</span>
        <div style={{ flex: '1', maxWidth: '60px', height: '1px', background: 'linear-gradient(to left, transparent, rgba(255,255,255,0.5))' }} />
      </div>
    );
  }
  if (cardStyle === 'festive') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        margin: '24px 0', justifyContent: 'center',
      }}>
        {[CORAL, GOLD, '#2BBAA0', GOLD, CORAL].map((c, i) => (
          <div key={i} style={{ width: '5px', height: '5px', borderRadius: '50%', background: c }} />
        ))}
      </div>
    );
  }
  if (cardStyle === 'botanical') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        margin: '24px 0', justifyContent: 'center',
      }}>
        <div style={{ flex: '1', maxWidth: '50px', height: '1px', background: 'linear-gradient(to right, transparent, #A8C5A0)' }} />
        <LeafSVG size={18} color="#5C7A5A" opacity={0.6} rotation={0} />
        <div style={{ flex: '1', maxWidth: '50px', height: '1px', background: 'linear-gradient(to left, transparent, #A8C5A0)' }} />
      </div>
    );
  }
  if (cardStyle === 'roseGold') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        margin: '24px 0', justifyContent: 'center',
      }}>
        <div style={{ flex: '1', maxWidth: '60px', height: '1px', background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.4))' }} />
        <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '10px', letterSpacing: '4px' }}>{'\u2764'}</span>
        <div style={{ flex: '1', maxWidth: '60px', height: '1px', background: 'linear-gradient(to left, transparent, rgba(255,255,255,0.4))' }} />
      </div>
    );
  }
  if (cardStyle === 'african') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        margin: '24px 0', justifyContent: 'center',
      }}>
        <div style={{ flex: '1', maxWidth: '40px', height: '2px', background: 'linear-gradient(to right, transparent, #CC6633)' }} />
        {[GOLD, '#CC6633', GOLD].map((c, i) => (
          <div key={i} style={{ width: '6px', height: '6px', background: c, transform: 'rotate(45deg)', opacity: 0.7 }} />
        ))}
        <div style={{ flex: '1', maxWidth: '40px', height: '2px', background: 'linear-gradient(to left, transparent, #CC6633)' }} />
      </div>
    );
  }
  if (cardStyle === 'midnight') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        margin: '24px 0', justifyContent: 'center',
      }}>
        <div style={{ flex: '1', maxWidth: '50px', height: '1px', background: 'linear-gradient(to right, transparent, rgba(192,192,255,0.4))' }} />
        <span style={{ color: '#C0C0FF', fontSize: '12px', letterSpacing: '6px', opacity: 0.7 }}>{'\u2726 \u2B51 \u2726'}</span>
        <div style={{ flex: '1', maxWidth: '50px', height: '1px', background: 'linear-gradient(to left, transparent, rgba(192,192,255,0.4))' }} />
      </div>
    );
  }
  if (cardStyle === 'garden') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        margin: '24px 0', justifyContent: 'center',
      }}>
        <div style={{ flex: '1', maxWidth: '50px', height: '1px', background: 'linear-gradient(to right, transparent, #B8D8B0)' }} />
        <DaisySVG size={18} color="#B8D8B0" centerColor="#FFD700" opacity={0.7} />
        <div style={{ flex: '1', maxWidth: '50px', height: '1px', background: 'linear-gradient(to left, transparent, #B8D8B0)' }} />
      </div>
    );
  }
  // minimal
  return (
    <div style={{ margin: '28px auto', width: '40px', height: '1px', background: '#E0E0E0' }} />
  );
}

/* ─── Format date ─── */
function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    // Handle both "2026-03-15" and "2026-03-15T00:00:00.000Z" formats
    const cleaned = String(dateStr).split('T')[0];
    const d = new Date(cleaned + 'T00:00:00');
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

/* ─── Helper: get border-top color for event info section ─── */
function getInfoBorderColor(cardStyle) {
  const map = {
    elegant: 'rgba(201,168,76,0.2)',
    floral: 'rgba(233,30,99,0.1)',
    golden: 'rgba(255,255,255,0.15)',
    minimal: '#EEEEEE',
    festive: 'rgba(227,66,92,0.15)',
    botanical: 'rgba(92,122,90,0.15)',
    roseGold: 'rgba(255,255,255,0.15)',
    african: 'rgba(204,102,51,0.2)',
    midnight: 'rgba(192,192,255,0.15)',
    garden: 'rgba(107,175,107,0.15)',
  };
  return map[cardStyle] || 'rgba(0,0,0,0.1)';
}

/* ════════════════════════════════════════════
   ThankYouCard
   ════════════════════════════════════════════ */
export default function ThankYouCard() {
  const { eventId, guestToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { injectStyles(); }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get(`/thank-you/${eventId}/${guestToken}`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.error || 'This thank you card could not be found.'))
      .finally(() => setLoading(false));
  }, [eventId, guestToken]);

  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = `I received a beautiful thank you card from ${data.event_title}! \u{1F49D} Check out Grand Gather for your next event: https://grandgatherregistry.co.za`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const handleShare = () => {
    const text = encodeURIComponent(
      `I received a beautiful thank you card from ${data.event_title}! \u{1F49D} Check out Grand Gather for your next event: https://grandgatherregistry.co.za`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  /* ─── Loading state ─── */
  if (loading) return <LoadingScreen />;

  /* ─── Error state ─── */
  if (error) {
    return (
      <div className="ty-page" style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: WARM_WHITE, color: CHARCOAL, padding: '24px',
        fontFamily: "'DM Sans', sans-serif", textAlign: 'center',
      }}>
        <FiAlertCircle size={48} color={CORAL} style={{ marginBottom: '16px' }} />
        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: '22px', marginBottom: '8px' }}>
          Card Not Found
        </h2>
        <p style={{ color: '#666', maxWidth: '320px', lineHeight: '1.6' }}>{error}</p>
        <a href="https://grandgatherregistry.co.za" style={{
          marginTop: '24px', color: CORAL, textDecoration: 'none',
          fontSize: '14px', fontWeight: 600,
        }}>
          Visit Grand Gather &rarr;
        </a>
      </div>
    );
  }

  /* ─── Resolve cover image: prefer invite_image > cover_image ─── */
  const cardImage = data.invite_image || data.cover_image;

  /* ─── Resolve theme ─── */
  const cardStyle = CARD_THEMES[data.card_style] ? data.card_style : 'elegant';
  const theme = CARD_THEMES[cardStyle];
  const isDark = DARK_BG_THEMES.includes(cardStyle);

  return (
    <div className="ty-page" style={{
      minHeight: '100vh',
      background: theme.pageBg,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      fontFamily: "'DM Sans', sans-serif",
      overflowX: 'hidden',
      position: 'relative',
    }}>
      {/* Floating particles */}
      <FloatingParticles cardStyle={cardStyle} />

      {/* Header */}
      <header style={{
        width: '100%', padding: '16px 20px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        position: 'relative', zIndex: 10,
      }}>
        <img src="/gift-icon-v2.png" alt="GGR" style={{
          height: '28px',
          filter: 'drop-shadow(0 2px 6px rgba(227,66,92,0.3))',
        }} />
        <span style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: '15px',
          color: isDark ? 'rgba(255,255,255,0.7)' :
                 cardStyle === 'floral' ? '#AD1457' : theme.subtitleColor,
          letterSpacing: '1px',
        }}>
          {data.event_title || 'Grand Gather'}
        </span>
      </header>

      {/* Card container */}
      <main style={{
        flex: 1, width: '100%', maxWidth: '480px',
        padding: '0 16px 24px', position: 'relative', zIndex: 5,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
      }}>
        <div style={{
          width: '100%', borderRadius: '20px',
          background: theme.cardBg,
          border: theme.borderStyle,
          boxShadow: theme.cardShadow,
          overflow: 'hidden', position: 'relative',
          animation: 'tyCardFadeIn 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards',
        }}>
          {/* Theme-specific decorations */}
          <CardDecorations cardStyle={cardStyle} theme={theme} />

          {/* Cover image */}
          {cardImage && (
            <div style={{
              width: '100%', height: '200px', position: 'relative', overflow: 'hidden',
            }}>
              <img
                src={cardImage}
                alt={data.event_title || 'Event'}
                style={{
                  width: '100%', height: '100%', objectFit: 'cover',
                  display: 'block',
                }}
              />
              <div style={{
                position: 'absolute', inset: 0,
                background: theme.overlayGradient,
              }} />
            </div>
          )}

          {/* Card body */}
          <div style={{
            padding: cardImage ? '28px 28px 32px' : '40px 28px 32px',
            textAlign: 'center', position: 'relative',
          }}>
            {/* "Thank You" heading */}
            <h1 style={{
              fontFamily: theme.fontHeading,
              fontSize: '36px', lineHeight: '1.2',
              color: theme.titleColor,
              margin: '0 0 4px',
              ...(cardStyle === 'elegant' ? {
                backgroundImage: `linear-gradient(135deg, ${GOLD}, #E8D48C, ${GOLD})`,
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'tyShimmer 4s linear infinite',
              } : {}),
              ...(cardStyle === 'roseGold' ? {
                backgroundImage: 'linear-gradient(135deg, #FFFFFF, #FFE4E8, #FFFFFF)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'tyShimmer 4s linear infinite',
              } : {}),
              ...(cardStyle === 'midnight' ? {
                backgroundImage: 'linear-gradient(135deg, #C0C0FF, #E8E8FF, #C0C0FF)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'tyShimmer 5s linear infinite',
              } : {}),
            }}>
              Thank You
            </h1>

            <ThemeDivider theme={theme} cardStyle={cardStyle} />

            {/* Message */}
            <p style={{
              fontSize: '15px', lineHeight: '1.75',
              color: theme.textColor, margin: '0 0 24px',
              whiteSpace: 'pre-line',
              maxWidth: '380px', marginLeft: 'auto', marginRight: 'auto',
            }}>
              {data.message}
            </p>

            <ThemeDivider theme={theme} cardStyle={cardStyle} />

            {/* Heart icon */}
            <div style={{
              margin: '0 0 16px',
              animation: 'tyHeartBeat 2s ease-in-out infinite',
              display: 'inline-block',
            }}>
              <FiHeart
                size={20}
                color={
                  cardStyle === 'golden' || cardStyle === 'roseGold' ? '#FFFFFF' :
                  cardStyle === 'midnight' ? '#C0C0FF' :
                  cardStyle === 'botanical' || cardStyle === 'garden' ? '#5C7A5A' :
                  cardStyle === 'african' ? '#CC6633' :
                  CORAL
                }
                fill={
                  cardStyle === 'golden' || cardStyle === 'roseGold' ? '#FFFFFF' :
                  cardStyle === 'midnight' ? '#C0C0FF' :
                  cardStyle === 'botanical' || cardStyle === 'garden' ? '#5C7A5A' :
                  cardStyle === 'african' ? '#CC6633' :
                  CORAL
                }
              />
            </div>

            {/* Signature */}
            {data.host_signature && (
              <p style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: '17px', color: theme.signatureColor,
                margin: '0 0 4px', fontStyle: 'italic',
              }}>
                {data.host_signature}
              </p>
            )}

            {/* Event info */}
            <div style={{
              marginTop: '20px', paddingTop: '16px',
              borderTop: `1px solid ${getInfoBorderColor(cardStyle)}`,
            }}>
              {data.event_title && (
                <p style={{
                  fontSize: '13px', fontWeight: 600,
                  color: theme.subtitleColor,
                  margin: '0 0 4px', letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}>
                  {data.event_title}
                </p>
              )}
              {data.event_date && (
                <>
                  <p style={{
                    fontSize: '12px', color: theme.subtitleColor,
                    margin: 0, opacity: 0.8,
                  }}>
                    {formatDate(data.event_date)}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Share actions — paired Copy + Send like Invitation */}
        <div style={{
          marginTop: '20px', display: 'flex', gap: '12px',
          alignItems: 'center', position: 'relative', zIndex: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              onClick={handleCopy}
              style={{
                width: '44px', height: '44px', borderRadius: '50%',
                border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)'}`,
                background: copied ? '#10b981' : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)'),
                color: copied ? '#fff' : (isDark ? '#e8e8ea' : '#1c1c1e'),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                if (!copied) e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)';
              }}
              onMouseLeave={e => {
                if (!copied) e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)';
              }}
            >
              {copied ? <span style={{ fontSize: '12px', fontWeight: 700 }}>OK</span> : <FiCopy size={16} />}
            </button>
            <span style={{
              fontSize: '12px', fontWeight: 600,
              color: copied ? '#10b981' : (isDark ? '#d0d0d4' : '#6b6b73'),
              transition: 'color 0.2s',
            }}>
              {copied ? 'Copied!' : 'Copy'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              onClick={handleShare}
              style={{
                width: '44px', height: '44px', borderRadius: '50%',
                background: '#25D366', border: 'none',
                color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(37,211,102,0.3)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,211,102,0.4)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(37,211,102,0.3)';
              }}
            >
              <FiShare2 size={16} />
            </button>
            <span style={{ fontSize: '12px', fontWeight: 600, color: '#25D366' }}>
              Send
            </span>
          </div>
        </div>
      </main>

      {/* Start your own event CTA */}
      <div style={{
        marginTop: '24px', padding: '0 16px', maxWidth: '480px', width: '100%',
        position: 'relative', zIndex: 10,
      }}>
        <a href="https://grandgatherregistry.co.za/register" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
          padding: '14px 24px', borderRadius: '14px',
          background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
          border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)',
          textDecoration: 'none', transition: 'all 0.2s',
        }}>
          <img src="/gift-icon-v2.png" alt="" style={{ height: '22px', opacity: 0.8 }} />
          <div style={{ textAlign: 'left' }}>
            <div style={{
              fontSize: '13px', fontWeight: 600,
              color: isDark ? 'rgba(255,255,255,0.7)' : '#444',
            }}>
              Planning an event?
            </div>
            <div style={{
              fontSize: '11px',
              color: isDark ? 'rgba(255,255,255,0.4)' : '#999',
            }}>
              Start your own event on Grand Gather &rarr;
            </div>
          </div>
        </a>
      </div>

      {/* Footer */}
      <footer style={{
        width: '100%', padding: '20px 16px 24px',
        textAlign: 'center', position: 'relative', zIndex: 10,
      }}>
        <p style={{
          fontSize: '11px',
          color: isDark ? 'rgba(255,255,255,0.35)' : '#AAAAAA',
          margin: 0, letterSpacing: '0.5px',
        }}>
          Powered by <strong>Grand Gather</strong> &mdash; Plan. Register. Celebrate.
        </p>
      </footer>
    </div>
  );
}
